import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  IconButton,
  Rating,
  Divider,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';
import { Restaurant } from '../types';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { S3_RESTAURANT_PLACEHOLDER } from '../config';

const RestaurantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useSelector((state: RootState) => state.auth);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [showRatingSuccess, setShowRatingSuccess] = useState(false);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [dishRatings, setDishRatings] = useState<{ [dishId: number]: number }>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const fetchRestaurantDetails = async () => {
      try {
        const response = await axios.get(`/api/restaurants/${id}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setRestaurant(response.data);
        setIsFavorite(response.data.is_favorite);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching restaurant details:', error);
        setLoading(false);
      }
    };

    if (token) {
      fetchRestaurantDetails();
    } else {
      navigate('/login');
    }
  }, [id, token, navigate]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  const handleRatingChange = async (event: React.SyntheticEvent, newValue: number | null) => {
    if (!token) {
      navigate('/login');
      return;
    }

    if (newValue === null) return;

    try {
      await axios.post(`/api/restaurants/${id}/rate/`, 
        { rating: newValue },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setUserRating(newValue);
      setShowRatingSuccess(true);
      
      // Refresh restaurant data to get updated average rating
      const response = await axios.get(`/api/restaurants/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRestaurant(response.data);
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`/api/auth/favorites/${id}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`/api/auth/favorites/`, { restaurant_id: id }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Error toggling favorite status:', error);
    }
  };

  const handleOpenInMaps = (type: 'google' | 'apple') => {
    if (!restaurant || !userLocation) return;
    const { latitude, longitude } = restaurant;
    const [userLat, userLng] = userLocation;
    if (type === 'google') {
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${latitude},${longitude}&travelmode=driving`,
        '_blank'
      );
    } else {
      window.open(
        `https://maps.apple.com/?daddr=${latitude},${longitude}&saddr=${userLat},${userLng}`,
        '_blank'
      );
    }
  };

  const handleCopyToClipboard = (type: 'address' | 'coordinates') => {
    if (!restaurant) return;
    const text = type === 'address' 
      ? restaurant.address 
      : `${restaurant.latitude},${restaurant.longitude}`;
    navigator.clipboard.writeText(text);
  };

  const handleDishVote = async (dishId: number, rating: number) => {
    if (!token || !restaurant) {
      setSnackbar({ open: true, message: 'You must be logged in to rate dishes.', severity: 'error' });
      return;
    }
    try {
      await axios.post(
        `/api/restaurants/${restaurant.id}/dishes/${dishId}/rate/`,
        { rating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDishRatings((prev) => ({ ...prev, [dishId]: rating }));
      setSnackbar({ open: true, message: 'Dish rating submitted!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to submit dish rating.', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!restaurant) {
    return (
      <Container>
        <Typography>Restaurant not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3, pb: 8 }}>
      <Box sx={{ position: 'relative', mb: 3 }}>
        <img
          src={restaurant.image_url || (restaurant.images && restaurant.images[0]?.url) || S3_RESTAURANT_PLACEHOLDER}
          alt={restaurant.name}
          style={{
            width: '100%',
            height: '300px',
            objectFit: 'cover',
            borderRadius: '8px',
          }}
        />
        <IconButton
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: 'white',
            '&:hover': { backgroundColor: 'white' },
          }}
          onClick={handleFavoriteToggle}
        >
          {isFavorite ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
        </IconButton>
        <IconButton
          sx={{
            position: 'absolute',
            top: 16,
            right: 64,
            backgroundColor: 'white',
            '&:hover': { backgroundColor: 'white' },
          }}
        >
          <ShareIcon />
        </IconButton>
      </Box>

      <Typography variant="h4" gutterBottom>
        {restaurant.name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Rating 
          value={restaurant.average_rating || 0} 
          precision={0.1} 
          readOnly 
        />
        <Typography variant="body1" sx={{ ml: 1 }}>
          {restaurant.average_rating?.toFixed(1) || 'No ratings'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          ({'$'.repeat(restaurant.price_range || 1)})
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Rate this restaurant
        </Typography>
        <Rating
          value={userRating}
          precision={0.5}
          onChange={handleRatingChange}
          size="large"
        />
      </Paper>

      <Box sx={{ mb: 3 }}>
        {restaurant.cuisines?.map((cuisine) => (
          <Chip
            key={cuisine.id}
            label={cuisine.name}
            sx={{ mr: 1, mb: 1 }}
          />
        ))}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              About
            </Typography>
            <Typography variant="body1" paragraph>
              {restaurant.description}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">{restaurant.address}</Typography>
            </Box>
            {restaurant.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">{restaurant.phone}</Typography>
              </Box>
            )}
            {restaurant.website && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WebsiteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  <a href={restaurant.website} target="_blank" rel="noopener noreferrer">
                    {restaurant.website}
                  </a>
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                color="success"
                startIcon={<LocationIcon />}
                onClick={() => handleOpenInMaps('google')}
                disabled={!userLocation}
              >
                Navigate with Google Maps
              </Button>
              <Button
                fullWidth
                size="large"
                variant="contained"
                color="info"
                startIcon={<LocationIcon />}
                onClick={() => handleOpenInMaps('apple')}
                disabled={!userLocation}
              >
                Navigate with Apple Maps
              </Button>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="primary"
                startIcon={<LocationIcon />}
                onClick={() => handleCopyToClipboard('address')}
              >
                Copy Address
              </Button>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="primary"
                startIcon={<LocationIcon />}
                onClick={() => handleCopyToClipboard('coordinates')}
              >
                Copy Coordinates
              </Button>
            </Box>
            {/* Dishes List */}
            {restaurant.dishes && restaurant.dishes.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Available Dishes
                </Typography>
                {restaurant.dishes.map((dish) => (
                  <Box key={dish.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ flex: 1 }}>{dish.name}</Typography>
                    <Rating
                      value={typeof dishRatings[dish.id] === 'number' ? dishRatings[dish.id] : (dish.average_rating || 0)}
                      precision={0.5}
                      onChange={(_, value) => value && handleDishVote(dish.id, value)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {dish.average_rating ? dish.average_rating.toFixed(1) : 'No ratings'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {restaurant.opening_hours && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Opening Hours
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {restaurant.opening_hours.monday?.open} - {restaurant.opening_hours.monday?.close}
                </Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={showRatingSuccess}
        autoHideDuration={3000}
        onClose={() => setShowRatingSuccess(false)}
      >
        <Alert onClose={() => setShowRatingSuccess(false)} severity="success">
          Rating submitted successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RestaurantDetailPage; 